/**
 * Stateless Message Processor
 * Pure functions for processing LLM events and updating messages
 * No internal state management - works with React state
 */

import type { 
  ThreadMessage, 
  TextContent, 
  ToolCallContent, 
  ThinkingContent, 
  SubSessionContent,
  LLMEvent, 
  MessageContent,
  FinishReason,
  SubgraphData,
} from '@/types/messages';
import type { AnswerCardState } from '@/types/answerCard.types';
import { EventType } from '@/types/messages';
import { logger } from '@/utils/logger';

export interface ProcessingContext {
  threadId: string;
  timestamp: number;
  eventOrder: number;
  sessionId?: string;
}

// Constants

/**
 * Process an LLMEvent and create/update a message
 * Returns a complete ThreadMessage that can be stored in the repository
 */
export function processLLMEvent(
  event: LLMEvent,
  context: ProcessingContext,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  const { threadId, timestamp, eventOrder, sessionId } = context;
  
  switch (event.type) {
    case EventType.AI_MESSAGE:
      return handleMessageStart(event, threadId, timestamp, eventOrder, sessionId, existingMessage);
    
    case EventType.AI_MESSAGE_CHUNK:
      // If there's no existing message, create one first (backend might start with chunks directly)
      if (!existingMessage) {
        const newMessage = handleMessageStart(
          { ...event, type: EventType.AI_MESSAGE, result: '' }, // Create empty message first
          threadId,
          timestamp,
          eventOrder,
          sessionId,
          null
        );
        // Handle the chunk with the newly created message
        const updated = handleTextChunk(event, newMessage, context);
        // If handleTextChunk returns null or the same message (empty chunk), return the new message
        return updated || newMessage;
      }
      return handleTextChunk(event, existingMessage, context);
    
    case EventType.THINKING:
      return handleThinkingStart(
        event, existingMessage, eventOrder, context
      );

    case EventType.THINKING_CHUNK:
      return handleThinkingChunk(event, existingMessage);
    
    case EventType.TOOL_CALL_START:
    case EventType.TOOL_CALL:
      return handleToolCallStart(event, existingMessage, context);
    
    case EventType.TOOL_CALL_CHUNK:
      return handleToolCallChunk(event, existingMessage);
    
    case EventType.TOOL_RESULT:
    case EventType.TOOL_END:
      return handleToolResult(event, existingMessage);
    
    case EventType.DATA:
      return handleDataEvent(event, existingMessage);
    
    case EventType.SUMMARY:
      return handleSummary(event, existingMessage, context);
    
    case EventType.ERROR:
      return handleError(event, existingMessage, threadId, timestamp, eventOrder);
    
    case EventType.FINISHED:
      return handleFinishWithData(event, existingMessage);

    case EventType.TURN_FINISH:
      return handleTurnFinishMetadata(event, existingMessage);
    
    default:
      return null;
  }
}

/**
 * Generate a consistent message ID for a thread/turn/session combination.
 * Pass isHierarchical=true for sub-agent events so they group under the
 * parent thread rather than their own session ID.
 */
export function generateMessageId(
  threadId: string,
  timestamp?: number,
  sessionId?: string,
  isHierarchical?: boolean
): string {
  const ts = timestamp || Date.now();
  if (!sessionId || isHierarchical) {
    return `message_${threadId}_${ts}`;
  }
  return `message_${sessionId}_${ts}`;
}

function handleMessageStart(
  event: LLMEvent,
  threadId: string,
  timestamp: number,
  eventOrder: number,
  sessionId?: string,
  existingMessage?: ThreadMessage | null
): ThreadMessage {
  const messageId = generateMessageId(threadId, timestamp, sessionId);
  if (existingMessage) {
    if (!event.result) {
      return existingMessage;
    }
    
    // Add initial text content
    const textPart: TextContent = {
      type: 'text',
      id: `${messageId}_text_${eventOrder}`,
      text: event.result,
      order: eventOrder,
      timestamp: new Date()
    };
    
    const newContent = addContentPart(existingMessage.content, textPart, sessionId, event);
    
    return {
      ...existingMessage,
      content: newContent,
      updatedAt: new Date()
    };
  }
  
  // Create new message with simplified structure
  const newMessage: ThreadMessage = {
    id: messageId,  // Simple string ID
    threadId,
    role: 'assistant',
    content: [],
    status: 'streaming',
    createdAt: new Date(),
    updatedAt: new Date(),
    metadata: {
      eventType: event.type,
      sessionId,
      custom: event.metadata || {}
    }
  };
  
  if (event.result) {
    const textPart: TextContent = {
      type: 'text',
      id: `${messageId}_text_${eventOrder}`,
      text: event.result,
      order: eventOrder,
      timestamp: new Date()
    };
    
    newMessage.content = addContentPart([], textPart, sessionId, event);
  }
  
  logger.debug('[StatelessProcessor] handleMessageStart: Created new message with status:', newMessage.status);
  
  return newMessage;
}

function handleTextChunk(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null,
  context?: ProcessingContext
): ThreadMessage | null {

  // Allow empty string chunks to pass through (they might be intentional)
  // Only warn and skip for null/undefined
  if (event.result === null || event.result === undefined) {
    logger.warn('[StatelessProcessor] handleTextChunk: No result in event');
    return existingMessage || null; // Ensure we return null if existingMessage is undefined
  }

  // Handle empty string - return existing message without modification
  if (event.result === '') {
    return existingMessage || null;
  }

  if (!existingMessage) {
    logger.warn('[StatelessProcessor] handleTextChunk: No existing message to update');
    return null;
  }
  
  const { sessionId, eventOrder } = context || {};
  
  if (isHierarchicalSession(event)) {
    const agentSessionId = deriveAgentSessionId(event, sessionId);
    const agentName = event.metadata?.agent_name || 'agent';
    const parentToolCallId = event.metadata?.parent_tool_call_id as string | undefined;
    const { index: subSessionIndex, content } = findOrClaimSubSession(
      existingMessage.content, agentSessionId, agentName, parentToolCallId
    );
    
    if (subSessionIndex >= 0) {
      const subSession = content[subSessionIndex] as SubSessionContent;
      const updatedSubContent = appendOrAddText(subSession.content, event.result, `${agentSessionId}_text_${eventOrder || Date.now()}`, eventOrder);
      content[subSessionIndex] = { ...subSession, content: updatedSubContent } as SubSessionContent;
      return { ...existingMessage, content, updatedAt: new Date() };
    } else {
      // No pending sub-session: link to the oldest unlinked outer tool call
      // and insert before it for visual co-location.
      const outerToolCallId = findUnlinkedOuterToolCallId(content, agentName);
      const insertIdx = outerToolCallId
        ? findOuterToolCallIndex(content, outerToolCallId)
        : -1;
      const textPart: TextContent = {
        type: 'text',
        id: `${agentSessionId}_text_${eventOrder || Date.now()}`,
        text: event.result,
        order: eventOrder || 0,
        timestamp: new Date(),
      };
      const newSubSession = makeSubSession(agentSessionId, agentName, [textPart], outerToolCallId, {
        title: findOuterToolCallTitle(content, outerToolCallId),
      });
      if (insertIdx >= 0) {
        content.splice(insertIdx, 0, newSubSession);
      } else {
        content.push(newSubSession);
      }
      return { ...existingMessage, content, updatedAt: new Date() };
    }
  } else {
    const updatedContent = appendOrAddText(existingMessage.content, event.result, `text_${eventOrder || Date.now()}`, eventOrder);
    return { ...existingMessage, content: updatedContent, updatedAt: new Date() };
  }
}

function handleThinkingStart(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null,
  eventOrder?: number,
  context?: ProcessingContext
): ThreadMessage | null {
  const thinkingPart: ThinkingContent = {
    type: 'thinking',
    id: event.id,
    content: event.result || '',
    isComplete: false,
    order: eventOrder,
    timestamp: new Date(),
  };

  if (!existingMessage) {
    // Create a minimal message to hold the thinking block when none exists yet.
    const { threadId, timestamp, sessionId } = context || {};
    if (!threadId || timestamp === undefined) {
      return null;
    }
    const messageId = generateMessageId(
      threadId,
      timestamp,
      sessionId,
      isHierarchicalSession(event),
    );
    const newMessage: ThreadMessage = {
      id: messageId,
      threadId,
      role: 'assistant',
      content: [],
      status: 'streaming',
      createdAt: new Date(timestamp),
      updatedAt: new Date(),
      metadata: { sessionId, custom: event.metadata || {} },
    };
    return {
      ...newMessage,
      content: addContentPart([], thinkingPart, sessionId, event),
    };
  }

  const { sessionId } = context || {};
  const updatedContent = addContentPart(
    existingMessage.content,
    thinkingPart,
    sessionId,
    event,
  );
  return { ...existingMessage, content: updatedContent, updatedAt: new Date() };
}

function handleThinkingChunk(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  if (!event.result || !existingMessage) {
    return null;
  }

  const newContent = [...existingMessage.content];

  if (isHierarchicalSession(event)) {
    // For sub-agent thinking: find the matching sub-session then the open
    // thinking block inside it.
    const agentSessionId = deriveAgentSessionId(event);
    const subSessionIndex = newContent.findIndex(
      (p) =>
        p.type === 'sub-session' &&
        (p as SubSessionContent).sessionId === agentSessionId,
    );
    if (subSessionIndex < 0) {
      return null;
    }
    const subSession = newContent[subSessionIndex] as SubSessionContent;
    const subContent = [...subSession.content];
    let thinkingIndex = -1;
    for (let i = subContent.length - 1; i >= 0; i--) {
      const p = subContent[i];
      if (p.type === 'thinking' && !(p as ThinkingContent).isComplete) {
        thinkingIndex = i;
        break;
      }
    }
    if (thinkingIndex < 0) {
      return null;
    }
    const thinking = subContent[thinkingIndex] as ThinkingContent;
    subContent[thinkingIndex] = {
      ...thinking,
      content: thinking.content + event.result,
    } as ThinkingContent;
    newContent[subSessionIndex] = {
      ...subSession,
      content: subContent,
    } as SubSessionContent;
    return { ...existingMessage, content: newContent, updatedAt: new Date() };
  }

  // Main-agent thinking: find the last open thinking block at top level.
  let thinkingIndex = -1;
  for (let i = newContent.length - 1; i >= 0; i--) {
    const p = newContent[i];
    if (p.type === 'thinking' && !(p as ThinkingContent).isComplete) {
      thinkingIndex = i;
      break;
    }
  }
  if (thinkingIndex < 0) {
    return null;
  }
  const thinking = newContent[thinkingIndex] as ThinkingContent;
  newContent[thinkingIndex] = {
    ...thinking,
    content: thinking.content + event.result,
  } as ThinkingContent;
  return { ...existingMessage, content: newContent, updatedAt: new Date() };
}

function handleToolCallStart(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null,
  context?: ProcessingContext
): ThreadMessage | null {
  if (!event.tool_call_id) {
    return null;
  }
  const title = event.title ?? undefined;
  
  if (!existingMessage) {
    const { threadId, timestamp, sessionId } = context || {};
    const messageId = generateMessageId(
      threadId || 'unknown', timestamp, sessionId, isHierarchicalSession(event)
    );
    existingMessage = {
      id: messageId,
      threadId: threadId || 'unknown',
      role: 'assistant',
      content: [],
      status: 'streaming',
      createdAt: new Date(),
      updatedAt: new Date(),
      metadata: {
        sessionId,
        custom: event.metadata || {}
      }
    };
  }
  
  const content = [...existingMessage.content];
  
  // Check if tool call already exists
  const existingIndex = content.findIndex(
    p => p.type === 'tool-call' && (p as ToolCallContent).toolCallId === event.tool_call_id
  );
  
  if (existingIndex >= 0) {
    // Update existing tool call
    const existing = content[existingIndex] as ToolCallContent;
    content[existingIndex] = {
      ...existing,
      toolName: event.tool_name || existing.toolName,
      title: title || existing.title,
      args: event.tool_params ? safeJsonParse(event.tool_params) : existing.args,
      artifact: event.artifact ?? existing.artifact,
      status: 'executing'
    } as ToolCallContent;
  } else {
    // Add new tool call
    const toolCallPart: ToolCallContent = {
      type: 'tool-call',
      id: event.id,
      toolCallId: event.tool_call_id,
      toolName: event.tool_name || 'unknown',
      ...(title ? { title } : {}),
      args: event.tool_params ? safeJsonParse(event.tool_params) : {},
      ...(event.artifact ? { artifact: event.artifact } : {}),
      status: 'pending',
      order: context?.eventOrder,
      timestamp: new Date()
    };
    
    const { sessionId } = context || {};
    if (isHierarchicalSession(event)) {
      // Inner subagent event: add to its sub-session
      const updatedContent = addContentPart(content, toolCallPart, sessionId, event);
      return {
        ...existingMessage,
        content: updatedContent,
        updatedAt: new Date()
      };
    }

    // Outer-level tool call — add to main content.
    // Pre-create a sub-session shell for subagent invocations so the render
    // layer always has a collapsed block to show, even if the subagent emits
    // no inner events before failing.  The pending_ prefix lets
    // findOrClaimSubSession promote it to a real sessionId when inner events
    // arrive.  The backend tags subagent TOOL_CALL events with is_subagent:
    // true in metadata, so no hardcoded name list is needed here.
    const toolName = event.tool_name ?? '';
    const isSubAgentInvocation = event.metadata?.is_subagent === true;

    if (isSubAgentInvocation && event.tool_call_id) {
      const proactiveSessionId = `pending_agent_${toolName}_${event.tool_call_id}`;
      const proactiveSubSession = makeSubSession(
        proactiveSessionId, toolName, [], event.tool_call_id,
        { order: context?.eventOrder, title }
      );
      content.push(proactiveSubSession);
    }

    content.push(toolCallPart);
  }
  
  return {
    ...existingMessage,
    content,
    updatedAt: new Date()
  };
}

function handleToolCallChunk(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  if (!existingMessage || !event.tool_call_id) {
    return null;
  }

  const parsedArgs = event.tool_params
    ? safeJsonParse(event.tool_params)
    : undefined;

  const updatedToolCallContent = updateToolCall(
    existingMessage.content,
    event.tool_call_id,
    { args: parsedArgs }
  );

  if (!updatedToolCallContent) {
    return null;
  }

  return {
    ...existingMessage,
    content: updatedToolCallContent,
    updatedAt: new Date(),
  };
}

function handleToolResult(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  if (!event.tool_call_id) {
    return null;
  }
  
  // If no existing message, we need to find the tool call across all messages
  // This handles cases where tool calls and results have different messageIds due to timing
  if (!existingMessage) {
    logger.debug(`[StatelessProcessor] TOOL_RESULT for ${event.tool_call_id} has no existing message`);
    return null; // Let the caller handle finding the right message
  }
  
  const updatedToolCallContent = updateToolCall(
    existingMessage.content,
    event.tool_call_id,
    {
      result: event.result,
      isError: event.is_error || false,
      status: event.is_error ? 'error' : 'complete',
      // A result without an artifact keeps the call's.
      ...(event.artifact ? { artifact: event.artifact } : {}),
    }
  );

  if (!updatedToolCallContent) {
    return null;
  }

  // If the result belongs to an outer-level tool-call that has a proactive
  // sub-session (created when the subagent TOOL_CALL arrived) and that
  // sub-session still has no content (the inner agent never emitted events),
  // propagate the result into the sub-session so the user can read it.
  // Match exclusively by metadata.toolCallId for per-invocation accuracy.
  const finalContent = updatedToolCallContent.map((part) => {
    if (part.type !== 'sub-session') return part;
    const ss = part as SubSessionContent;
    if (ss.metadata?.toolCallId !== event.tool_call_id || ss.content.length > 0) {
      return ss;
    }
    const resultStr =
      typeof event.result === 'string' ? event.result : '';
    const resultContent: TextContent[] = resultStr
      ? [
          {
            type: 'text' as const,
            id: `${ss.sessionId}_result_${Date.now()}`,
            text: resultStr,
            order: 0,
            timestamp: new Date(),
          },
        ]
      : [];
    return {
      ...ss,
      content: resultContent,
      status: (event.is_error ? 'error' : 'complete') as SubSessionContent['status'],
    } as SubSessionContent;
  });

  return {
    ...existingMessage,
    content: finalContent,
    updatedAt: new Date()
  };
}

function handleDataEvent(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  if (!existingMessage) {
    logger.debug('[DATA_EVENT] No existing message for DATA event');
    return null;
  }

  const updatedMetadata = { ...existingMessage.metadata };

  // Handle token usage stats
  if (event.id === 'token_usage' && event.metadata) {
    const m = event.metadata;
    if (
      typeof m.totalTokens === 'number' &&
      typeof m.maxTokens === 'number' &&
      typeof m.percentage === 'number' &&
      typeof m.tokensRemaining === 'number' &&
      typeof m.modelCalls === 'number'
    ) {
      updatedMetadata.tokenUsage = {
        totalTokens: m.totalTokens,
        maxTokens: m.maxTokens,
        percentage: m.percentage,
        tokensRemaining: m.tokensRemaining,
        modelCalls: m.modelCalls,
      };
      logger.debug(
        '[DATA_EVENT] Updated token usage stats:',
        updatedMetadata.tokenUsage,
      );
    }
  }

  // Extract availableIds from event metadata and store in message metadata
  if (event.metadata?.availableIds && Array.isArray(event.metadata.availableIds)) {
    const existingIds = existingMessage.metadata?.availableIds || [];
    const newIds = Array.from(new Set([...existingIds, ...event.metadata.availableIds]));
    updatedMetadata.availableIds = newIds;
  }

  return {
    ...existingMessage,
    metadata: updatedMetadata,
    updatedAt: new Date()
  };
}

function handleSummary(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null,
  context?: ProcessingContext
): ThreadMessage | null {
  if (!event.result) {
    logger.warn('[StatelessProcessor] handleSummary: No result in summary event');
    return existingMessage || null;
  }

  const { threadId, timestamp, eventOrder, sessionId } = context || {};
  
  // If no existing message, create a new one for the summary
  if (!existingMessage) {
    if (!threadId || timestamp === undefined || eventOrder === undefined) {
      logger.warn('[StatelessProcessor] handleSummary: Cannot create message without context');
      return null;
    }
    
    const messageId = generateMessageId(
      threadId, timestamp, sessionId, isHierarchicalSession(event)
    );
    const summaryContent: TextContent = {
      type: 'text',
      id: `${messageId}_summary_${eventOrder}`,
      text: event.result,
      order: eventOrder,
      timestamp: new Date(timestamp)
    };
    
    const summaryMessage: ThreadMessage = {
      id: messageId,
      threadId,
      role: 'assistant',
      content: [summaryContent],
      status: 'complete',
      createdAt: new Date(timestamp),
      updatedAt: new Date(),
      metadata: {
        sequenceNumber: eventOrder,
        eventType: event.type,
        sessionId,
        custom: {
          ...event.metadata,
          isSummary: true,
          summarizedMessageIds: event.metadata?.summarizedMessageIds || [],
          summaryData: event.data || event.metadata?.summaryData
        }
      },
      data: event.data
    };
    
    logger.debug('[StatelessProcessor] handleSummary: Created new summary message');
    return summaryMessage;
  }
  
  const summaryContent: TextContent = {
    type: 'text',
    id: `${existingMessage.id}_summary_${eventOrder || Date.now()}`,
    text: event.result,
    order: eventOrder,
    timestamp: new Date()
  };

  let content: MessageContent[];

  // Check if summary should be added to a sub-session
  if (isHierarchicalSession(event)) {
    const agentSessionId = deriveAgentSessionId(event, sessionId);
    const agentName = event.metadata?.agent_name || 'agent';
    const parentToolCallId = event.metadata?.parent_tool_call_id as string | undefined;
    const { index: subSessionIndex, content: claimedContent } = findOrClaimSubSession(
      existingMessage.content, agentSessionId, agentName, parentToolCallId
    );
    content = claimedContent;

    if (subSessionIndex >= 0) {
      const subSession = content[subSessionIndex] as SubSessionContent;
      content[subSessionIndex] = {
        ...subSession,
        content: [...subSession.content, summaryContent]
      } as SubSessionContent;
    } else {
      // No pending sub-session: link to the oldest unlinked outer tool call
      // and insert before it for visual co-location.
      const outerToolCallId = findUnlinkedOuterToolCallId(content, agentName);
      const insertIdx = outerToolCallId
        ? findOuterToolCallIndex(content, outerToolCallId)
        : -1;
      const newSubSession = makeSubSession(
        agentSessionId, agentName, [summaryContent], outerToolCallId,
        { status: 'complete', title: findOuterToolCallTitle(content, outerToolCallId) }
      );
      if (insertIdx >= 0) {
        content.splice(insertIdx, 0, newSubSession);
      } else {
        content.push(newSubSession);
      }
    }
  } else {
    content = [...existingMessage.content, summaryContent];
  }
  
  // Update metadata with summary information
  const updatedMetadata = {
    ...existingMessage.metadata,
    custom: {
      ...existingMessage.metadata?.custom,
      ...event.metadata,
      isSummary: true,
      summarizedMessageIds: event.metadata?.summarizedMessageIds || [],
      summaryData: event.data || event.metadata?.summaryData
    }
  };
  
  return {
    ...existingMessage,
    content,
    metadata: updatedMetadata,
    data: event.data || existingMessage.data,
    updatedAt: new Date()
  };
}

function handleError(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null,
  threadId?: string,
  timestamp?: number,
  eventOrder?: number
): ThreadMessage | null {
  // If there's an existing message, update it with error status
  if (existingMessage) {
    return {
      ...existingMessage,
      status: 'error',
      metadata: {
        ...existingMessage.metadata,
        custom: {
          ...existingMessage.metadata?.custom,
          error: event.error || event.result
        }
      },
      updatedAt: new Date()
    };
  }

  // If no existing message, create a new error message
  // This handles cases where backend sends ERROR event before any other events
  if (threadId && timestamp !== undefined && eventOrder !== undefined) {
    const errorContent: TextContent = {
      type: 'text',
      id: `${event.id || 'error'}_text_${eventOrder}`,
      text: `Error: ${event.error || event.result || 'An unknown error occurred'}`,
      order: eventOrder,
      timestamp: new Date(timestamp)
    };

    return {
      id: event.id || `error-${Date.now()}`,
      role: 'assistant',
      content: [errorContent],
      threadId,
      status: 'error',
      createdAt: new Date(timestamp),
      updatedAt: new Date(),
      metadata: {
        sequenceNumber: eventOrder,
        eventType: event.type,
        custom: {
          error: event.error || event.result
        }
      }
    };
  }

  // If we don't have enough context to create a message, log and return null
  logger.error('Received ERROR event without existing message or context', { event });
  return null;
}

function handleFinishWithData(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  if (!existingMessage) {
    logger.debug('[FINISH_STATE] handleFinishWithData: No existing message to complete');
    logger.debug('[FINISH_STATE] Event data:', event.data);
    return null;
  }

  logger.debug('[FINISH_STATE] Processing FINISHED event with data:', event.data);

  const isSubAgentEvent = event.metadata?.is_subagent === true;
  const eventSessionId = isSubAgentEvent
    ? deriveAgentSessionId(event)
    : undefined;

  const content = existingMessage.content.map(part => {
    if (part.type === 'thinking') {
      return { ...part, isComplete: true } as ThinkingContent;
    }
    if (part.type === 'sub-session') {
      const subSession = part as SubSessionContent;
      const isMatchingSubSession =
        subSession.status === 'streaming' &&
        (!eventSessionId || subSession.sessionId === eventSessionId);
      if (!isMatchingSubSession) {
        return subSession;
      }
      return {
        ...subSession,
        status: 'complete' as const,
      } as SubSessionContent;
    }
    return part;
  });

  // Handle availableIds from finished event metadata
  const updatedMetadata = { ...existingMessage.metadata };
  if (event.metadata?.availableIds && Array.isArray(event.metadata.availableIds)) {
    const existingIds = existingMessage.metadata?.availableIds || [];
    const newIds = Array.from(new Set([...existingIds, ...event.metadata.availableIds]));
    updatedMetadata.availableIds = newIds;
    logger.debug('[FINISH_STATE] Added availableIds to metadata:', newIds);
  } else {
    logger.debug('[FINISH_STATE] No availableIds in event metadata');
  }

  // Per-message subgraph payload (replaces the per-render Neo4j call).
  if (event.metadata?.subgraph) {
    updatedMetadata.subgraph = event.metadata.subgraph as SubgraphData;
    logger.debug(
      '[FINISH_STATE] Attached subgraph to metadata:',
      `nodes=${updatedMetadata.subgraph?.nodes?.length ?? 0}`,
      `rels=${updatedMetadata.subgraph?.relationships?.length ?? 0}`,
    );
  }

  if (event.metadata?.answerCard) {
    updatedMetadata.answerCard = event.metadata.answerCard as AnswerCardState;
  }

  return {
    ...existingMessage,
    content,
    status: 'complete',
    metadata: updatedMetadata,
    updatedAt: new Date()
  };
}

/**
 * Handle TURN_FINISH events emitted by parse_chat_model_end.
 *
 * TURN_FINISH carries per-LLM-call metadata (finish_reason, token usage).
 * It accumulates this metadata onto the existing message without marking it
 * complete or completing content parts -- only FINISHED does that.
 */
function handleTurnFinishMetadata(
  event: LLMEvent,
  existingMessage?: ThreadMessage | null
): ThreadMessage | null {
  if (!existingMessage) {
    logger.debug('[TURN_FINISH] No existing message; metadata discarded');
    return null;
  }

  const isSubAgentEvent = event.metadata?.is_subagent === true;
  const eventSessionId = isSubAgentEvent
    ? deriveAgentSessionId(event)
    : undefined;

  const finishReason = event.metadata?.finish_reason as
    | FinishReason
    | 'stop'
    | undefined;
  const llmTokens = event.metadata?.totalTokens
    ? {
        inputTokens: (event.metadata.inputTokens as number) ?? 0,
        outputTokens: (event.metadata.outputTokens as number) ?? 0,
        totalTokens: event.metadata.totalTokens as number,
      }
    : undefined;

  // For sub-agent events, attach finish info to the matching sub-session.
  const content = existingMessage.content.map(part => {
    if (part.type !== 'sub-session') {
      return part;
    }
    const subSession = part as SubSessionContent;
    const isMatchingSubSession =
      subSession.status === 'streaming' &&
      (!eventSessionId || subSession.sessionId === eventSessionId);
    if (!isSubAgentEvent || !isMatchingSubSession) {
      return subSession;
    }
    if (finishReason && finishReason !== 'stop') {
      const updates: Partial<SubSessionContent> = {
        metadata: {
          ...subSession.metadata,
          finishReason,
          ...(llmTokens ? { llmTokens } : {}),
        },
      };
      logger.debug('[TURN_FINISH] Stored finishReason on sub-session:', finishReason);
      return { ...subSession, ...updates } as SubSessionContent;
    }
    return subSession;
  });

  // For main-agent TURN_FINISH events, store finishReason and llmTokens on
  // the message metadata so the top-level banner can display them.
  const updatedMetadata = { ...existingMessage.metadata };
  if (!isSubAgentEvent) {
    if (finishReason) {
      updatedMetadata.finishReason = finishReason;
      logger.debug('[TURN_FINISH] Stored finishReason:', finishReason);
    }
    if (llmTokens) {
      updatedMetadata.llmTokens = llmTokens;
      logger.debug('[TURN_FINISH] Stored llmTokens:', llmTokens);
    }
  }

  return {
    ...existingMessage,
    content,
    metadata: updatedMetadata,
    updatedAt: new Date(),
  };
}

// Helper functions

interface ClaimResult {
  index: number;
  content: MessageContent[];
}

interface MakeSubSessionOptions {
  status?: SubSessionContent['status'];
  /** The title of the tool call that started the session, when known. */
  title?: string;
  order?: number;
}

function makeSubSession(
  sessionId: string,
  agentName: string,
  initialContent: MessageContent[],
  outerToolCallId?: string,
  options: MakeSubSessionOptions = {}
): SubSessionContent {
  const { status = 'streaming', title, order } = options;
  return {
    type: 'sub-session',
    id: `sub_session_${sessionId}`,
    sessionId,
    agentName,
    content: initialContent,
    isCollapsed: true,
    status,
    ...(title ? { title } : {}),
    timestamp: new Date(),
    ...(order !== undefined ? { order } : {}),
    metadata: outerToolCallId ? { toolCallId: outerToolCallId } : undefined,
  };
}

/** Derive sessionId for sub-session matching.
 * Includes sub_session_id when available so that separate invocations of the
 * same subagent (e.g. two consecutive search_agent calls) each get their own
 * sub-session block instead of merging into one. */
function deriveAgentSessionId(event: LLMEvent | undefined, contextSessionId?: string): string {
  const meta = event?.metadata;
  if (meta?.agent_name) {
    if (meta.sub_session_id) return `agent_${meta.agent_name}_${meta.sub_session_id}`;
    return `agent_${meta.agent_name}`;
  }
  return contextSessionId ?? 'agent_unknown';
}

/** Find sub-session by exact sessionId, or claim a pending proactive
 * sub-session for this agent and rename it.
 *
 * Claiming priority:
 *   1. Exact sessionId match (already promoted in a previous event).
 *   2. Pending proactive whose metadata.toolCallId matches the
 *      parent_tool_call_id carried by the inner event — an exact link
 *      provided by the backend since the subagent now injects its own
 *      tool_call_id into the RunnableConfig.
 *   3. Oldest pending proactive for the same agent (legacy fallback when
 *      parent_tool_call_id is absent, e.g. replayed from DB).
 *
 * Returns a ClaimResult with the index and a new content array. The content
 * array is always a fresh copy so callers can mutate it freely. When a
 * pending sub-session is claimed its sessionId is updated in the returned
 * array — the input array is never modified. */
function findOrClaimSubSession(
  content: readonly MessageContent[],
  agentSessionId: string,
  agentName: string,
  parentToolCallId?: string,
): ClaimResult {
  // 1. Exact match (already claimed/promoted in a prior event).
  const exactIndex = content.findIndex(
    (p) =>
      p.type === 'sub-session' &&
      (p as SubSessionContent).sessionId === agentSessionId &&
      (p as SubSessionContent).status !== 'complete'
  );
  if (exactIndex >= 0) return { index: exactIndex, content: [...content] };

  const pendingPrefix = `pending_agent_${agentName}_`;

  // 2. Exact proactive match via parent_tool_call_id (backend-provided link).
  if (parentToolCallId) {
    const exactPendingIndex = content.findIndex(
      (p) =>
        p.type === 'sub-session' &&
        (p as SubSessionContent).agentName === agentName &&
        (p as SubSessionContent).sessionId.startsWith(pendingPrefix) &&
        (p as SubSessionContent).metadata?.toolCallId === parentToolCallId
    );
    if (exactPendingIndex >= 0) {
      const newContent = content.map((item, i) =>
        i === exactPendingIndex
          ? ({ ...(item as SubSessionContent), sessionId: agentSessionId } as SubSessionContent)
          : item
      ) as MessageContent[];
      return { index: exactPendingIndex, content: newContent };
    }
  }

  // 3. Oldest pending proactive fallback (for replayed/historical messages).
  const oldestPendingIndex = content.findIndex(
    (p) =>
      p.type === 'sub-session' &&
      (p as SubSessionContent).agentName === agentName &&
      (p as SubSessionContent).sessionId.startsWith(pendingPrefix)
  );
  if (oldestPendingIndex >= 0) {
    const newContent = content.map((item, i) =>
      i === oldestPendingIndex
        ? ({ ...(item as SubSessionContent), sessionId: agentSessionId } as SubSessionContent)
        : item
    ) as MessageContent[];
    return { index: oldestPendingIndex, content: newContent };
  }

  return { index: -1, content: [...content] };
}

/** Find the toolCallId of the oldest outer tool call for this agent that
 * is not yet linked to any sub-session.  Used to retroactively attach
 * metadata.toolCallId when a sub-session is created from inner events
 * (i.e. no proactive pending sub-session existed). */
function findUnlinkedOuterToolCallId(
  content: MessageContent[],
  agentName: string,
): string | undefined {
  const linkedIds = new Set<string>();
  content.forEach((p) => {
    if (p.type !== 'sub-session') return;
    const ss = p as SubSessionContent;
    if (ss.agentName === agentName && ss.metadata?.toolCallId) {
      linkedIds.add(ss.metadata.toolCallId);
    }
  });
  for (const p of content) {
    if (p.type !== 'tool-call') continue;
    const tc = p as ToolCallContent;
    if (tc.toolName !== agentName) continue;
    if (!tc.toolCallId || linkedIds.has(tc.toolCallId)) continue;
    return tc.toolCallId; // oldest unlinked tool call
  }
  return undefined;
}

/** Return the index of the outer tool call with this id, or -1. */
function findOuterToolCallIndex(
  content: MessageContent[],
  toolCallId: string,
): number {
  return content.findIndex(
    (p) =>
      p.type === 'tool-call' &&
      (p as ToolCallContent).toolCallId === toolCallId
  );
}

/** Return the title of the outer tool call with this id, or undefined. */
function findOuterToolCallTitle(
  content: MessageContent[],
  toolCallId: string | undefined,
): string | undefined {
  if (!toolCallId) return undefined;
  const tc = content.find(
    (p) =>
      p.type === 'tool-call' &&
      (p as ToolCallContent).toolCallId === toolCallId
  ) as ToolCallContent | undefined;
  return tc?.title;
}

function insertIntoSubSession(
  content: readonly MessageContent[],
  part: MessageContent,
  sessionId?: string,
  event?: LLMEvent
): MessageContent[] {
  const agentSessionId = deriveAgentSessionId(event, sessionId);
  const agentName = (event?.metadata?.agent_name as string)
    || agentSessionId.split('_').slice(1).join('_')
    || 'agent';
  const parentToolCallId = event?.metadata?.parent_tool_call_id as string | undefined;

  const { index: subSessionIndex, content: workingContent } = findOrClaimSubSession(
    content, agentSessionId, agentName, parentToolCallId
  );

  if (subSessionIndex >= 0) {
    const subSession = workingContent[subSessionIndex] as SubSessionContent;
    workingContent[subSessionIndex] = {
      ...subSession,
      content: [...subSession.content, part]
    } as SubSessionContent;
  } else {
    // No pending sub-session: link to the oldest unlinked outer tool call
    // and insert before it so the collapsed view sits adjacent to the call.
    const outerToolCallId = findUnlinkedOuterToolCallId(workingContent, agentName);
    const insertIdx = outerToolCallId
      ? findOuterToolCallIndex(workingContent, outerToolCallId)
      : -1;
    const newSubSession = makeSubSession(agentSessionId, agentName, [part], outerToolCallId, {
      title: findOuterToolCallTitle(workingContent, outerToolCallId),
    });
    if (insertIdx >= 0) {
      workingContent.splice(insertIdx, 0, newSubSession);
    } else {
      workingContent.push(newSubSession);
    }
  }

  return workingContent;
}

function addContentPart(
  content: readonly MessageContent[],
  part: MessageContent,
  sessionId?: string,
  event?: LLMEvent
): MessageContent[] {
  if (isHierarchicalSession(event)) {
    return insertIntoSubSession(content, part, sessionId, event);
  }
  return [...content, part];
}

function appendOrAddText(
  content: readonly MessageContent[],
  text: string,
  id: string,
  order?: number
): MessageContent[] {
  const newContent = [...content];
  
  // Find last text part
  let lastTextIndex = -1;
  for (let i = newContent.length - 1; i >= 0; i--) {
    if (newContent[i].type === 'text') {
      lastTextIndex = i;
      break;
    }
  }
  
  if (lastTextIndex >= 0 && lastTextIndex === newContent.length - 1) {
    // Concatenate to last text part if it's the last item
    const lastText = newContent[lastTextIndex] as TextContent;
    newContent[lastTextIndex] = {
      ...lastText,
      text: lastText.text + text
    } as TextContent;
  } else {
    // Add new text part
    const newTextPart: TextContent = {
      type: 'text',
      id,
      text,
      order,
      timestamp: new Date()
    };
    newContent.push(newTextPart);
  }
  
  return newContent;
}

function updateToolCall(
  content: readonly MessageContent[],
  toolCallId: string,
  updates: Partial<ToolCallContent>
): MessageContent[] | null {
  const newContent = [...content];
  
  // Check main content
  const toolIndex = newContent.findIndex(
    p => p.type === 'tool-call' && (p as ToolCallContent).toolCallId === toolCallId
  );
  
  if (toolIndex >= 0) {
    const tool = newContent[toolIndex] as ToolCallContent;
    newContent[toolIndex] = { ...tool, ...updates };
    return newContent;
  }
  
  // Check sub-sessions
  for (let i = 0; i < newContent.length; i++) {
    if (newContent[i].type === 'sub-session') {
      const subSession = newContent[i] as SubSessionContent;
      const subToolIndex = subSession.content.findIndex(
        p => p.type === 'tool-call' && (p as ToolCallContent).toolCallId === toolCallId
      );
      
      if (subToolIndex >= 0) {
        const subContent = [...subSession.content];
        const tool = subContent[subToolIndex] as ToolCallContent;
        subContent[subToolIndex] = { ...tool, ...updates };
        
        newContent[i] = {
          ...subSession,
          content: subContent
        } as SubSessionContent;
        
        return newContent;
      }
    }
  }
  
  return null;
}

function isHierarchicalSession(event?: LLMEvent): boolean {
  return event?.metadata?.is_subagent === true;
}


function safeJsonParse(jsonString: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(jsonString);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return parsed as Record<string, unknown>;
    }
    return {};
  } catch {
    return {};
  }
}